import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');
  const [isOpen, setIsOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOnOutsidePress = (event: MouseEvent) => {
      if (!switcherRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const changeLanguage = (next: string) => {
    if (next === i18n.language) return;
    i18n.changeLanguage(next);
    document.documentElement.lang = next === 'zh' ? 'zh-CN' : 'en';
    setIsOpen(false);
  };

  const currentLanguage = isZh ? '中文' : 'English';

  return (
    <div ref={switcherRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex h-10 min-w-[7.25rem] items-center justify-between gap-2 rounded-full border border-white/15 bg-white/5 px-3 text-xs font-semibold text-tarot-starLight/80 transition-colors hover:border-tarot-gold/40 hover:text-tarot-gold focus-visible:border-tarot-gold focus-visible:text-tarot-gold focus-visible:outline-none"
        aria-label={isZh ? '选择语言' : 'Select language'}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls="language-options"
      >
        <span className="inline-flex items-center gap-2">
          <Languages className="h-4 w-4" aria-hidden="true" />
          <span>{currentLanguage}</span>
        </span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          id="language-options"
          role="menu"
          aria-label={isZh ? '选择语言' : 'Select language'}
          className="absolute right-0 top-[calc(100%+0.5rem)] z-50 min-w-full overflow-hidden rounded-2xl border border-tarot-gold/40 bg-[linear-gradient(145deg,rgba(24,28,40,0.98),rgba(9,12,20,0.99))] p-1.5 shadow-[0_18px_44px_rgba(0,0,0,0.5)]"
        >
          {[
            { code: 'en', label: 'English' },
            { code: 'zh', label: '中文' },
          ].map((language) => {
            const selected = language.code === (isZh ? 'zh' : 'en');
            return (
              <button
                key={language.code}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => changeLanguage(language.code)}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-xs font-semibold transition-colors ${selected ? 'bg-tarot-gold/15 text-tarot-gold shadow-[inset_0_0_0_1px_rgba(214,177,105,0.12)]' : 'text-tarot-starLight/75 hover:bg-white/8 hover:text-tarot-starLight'}`}
              >
                <span className="flex-1">{language.label}</span>
                {selected && <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
